import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { collection, doc, getDocs, getFirestore, limit, orderBy, query, Timestamp, setDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import swal from 'sweetalert';
import { app } from '../../firebase';
import { buildReceiptEmail } from '../../utils/emailTemplates';
import '../dash.css';

const SESSION_LIMIT = 150;
const OFFLINE_LIMIT = 150;
const UNMATCHED_LIMIT = 100;
const INCOME_LIMIT = 150;

const toTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value?.seconds === 'number') return value.seconds * 1000;

  const parsedDate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
};

const sortByNewest = (items, getValue) => (
  [...items].sort((left, right) => getValue(right) - getValue(left))
);

const matchesSearch = (searchTerm, values) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return values
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(normalizedSearch);
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizePhone = (value) => String(value || '').replace(/[^\d]/g, '');

const getStudentDisplayName = (student = {}) => student.bio || student.studentName || student.fullName || student.name || 'Unknown student';

const getStudentIdentity = (student = {}) => String(student.userId || student.studentId || student.id || '').trim();

const getStudentStatusLabel = (student = {}) => student.userStatus || student.status || 'Status unavailable';

const buildStudentSearchBlob = (student = {}) => [
  student.bio,
  student.fullName,
  student.name,
  student.userId,
  student.studentId,
  student.phoneNumber,
  student.email,
  student.userEmail,
  student.course,
  student.userStatus,
  student.admissionNumber
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const scoreStudentMatch = (payment = {}, student = {}) => {
  const paymentName = normalizeText(payment.payerName || payment.studentName || payment.name);
  const paymentPhone = normalizePhone(payment.msisdn || payment.phoneNumber || payment.payerPhone || payment.payerMsisdn);
  const paymentRef = normalizeText(payment.billRefNumber || payment.rawBillRefNumber || payment.transId || payment.reference || payment.note);
  const studentName = normalizeText(getStudentDisplayName(student));
  const studentPhone = normalizePhone(student.phoneNumber || student.phone || student.mobile || student.msisdn);
  const studentId = normalizeText(getStudentIdentity(student));
  const admissionNumber = normalizeText(student.admissionNumber || student.admissionNo || student.studentNumber);

  let score = 0;
  const reasons = [];

  if (paymentRef && studentId && paymentRef === studentId) {
    score += 100;
    reasons.push('reference matches student id');
  }

  if (paymentPhone && studentPhone) {
    const paymentTail = paymentPhone.slice(-9);
    const studentTail = studentPhone.slice(-9);

    if (paymentPhone === studentPhone || paymentTail === studentTail) {
      score += 95;
      reasons.push('phone number matches');
    }
  }

  if (paymentRef && admissionNumber && paymentRef === admissionNumber) {
    score += 90;
    reasons.push('reference matches admission number');
  }

  if (paymentName && studentName) {
    const paymentWords = paymentName.split(/\s+/).filter((word) => word.length > 2);
    const studentWords = studentName.split(/\s+/).filter((word) => word.length > 2);
    const wordOverlap = paymentWords.filter((word) => studentWords.includes(word)).length;

    if (paymentName === studentName) {
      score += 70;
      reasons.push('name matches exactly');
    } else if (wordOverlap > 0) {
      score += 40 + (wordOverlap * 8);
      reasons.push('name partially matches');
    }
  }

  if (paymentRef && studentName && paymentRef.includes(studentId)) {
    score += 35;
    reasons.push('reference contains student id');
  }

  if (paymentRef && buildStudentSearchBlob(student).includes(paymentRef)) {
    score += 25;
    reasons.push('reference appears in student record');
  }

  if (score === 0 && paymentName && buildStudentSearchBlob(student).includes(paymentName)) {
    score += 15;
    reasons.push('payer name appears in student record');
  }

  return {
    score,
    reasons
  };
};

function PaymentAuditComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('action-needed');
  const [searchTerm, setSearchTerm] = useState('');
  const [sessions, setSessions] = useState([]);
  const [offlineTransactions, setOfflineTransactions] = useState([]);
  const [unmatchedPayments, setUnmatchedPayments] = useState([]);
  const [incomeAuditRows, setIncomeAuditRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [matchModalShow, setMatchModalShow] = useState(false);
  const [selectedUnmatchedPayment, setSelectedUnmatchedPayment] = useState(null);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [savingMatch, setSavingMatch] = useState(false);

  const loadAuditData = useCallback(async () => {
    try {
      const [
        sessionsSnapshot,
        offlineSnapshot,
        unmatchedSnapshot,
        incomeSnapshot,
        studentsSnapshot
      ] = await Promise.all([
        getDocs(query(collection(db, 'StudentPaymentSessions'), orderBy('createdAt', 'desc'), limit(SESSION_LIMIT))),
        getDocs(query(collection(db, 'MpesaC2BTransactions'), orderBy('paymentDate', 'desc'), limit(OFFLINE_LIMIT))),
        getDocs(query(collection(db, 'UnmatchedFeePayments'), orderBy('createdAt', 'desc'), limit(UNMATCHED_LIMIT))),
        getDocs(query(collection(db, 'Income'), orderBy('paymentDate', 'desc'), limit(INCOME_LIMIT))),
        getDocs(collection(db, 'users'))
      ]);

      setSessions(sessionsSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
      setOfflineTransactions(offlineSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
      setUnmatchedPayments(unmatchedSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
      setIncomeAuditRows(
        incomeSnapshot.docs
          .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
          .filter((item) => ['student_stk', 'offline_c2b'].includes(item.paymentChannel))
      );
      setStudents(studentsSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    } catch (error) {
      console.error('Error loading payment audit data:', error);
      throw error;
    }
  }, [db]);

  useEffect(() => {
    const fetchAuditData = async () => {
      try {
        setLoading(true);
        await loadAuditData();
      } catch (error) {
        console.error('Error loading payment audit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, [loadAuditData]);

  const formatDateTime = useCallback((value) => {
    const timestamp = toTimestampMs(value);
    if (!timestamp) return '-';

    return new Date(timestamp).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatAmount = useCallback((value, currency = 'KES') => {
    const numericValue = Number(value || 0);
    return `${currency || 'KES'} ${numericValue.toLocaleString('en-KE')}`;
  }, []);

  const formatPaymentType = useCallback((value) => {
    if (value === 'admission') return 'Admission';
    if (value === 'tuition') return 'Tuition';
    if (value === 'admission_fee') return 'Admission Fee';
    if (value === 'tuition_fee') return 'Tuition Fee';
    return value ? value.replace(/_/g, ' ') : '-';
  }, []);

  const formatChannel = useCallback((value) => {
    if (value === 'student_stk') return 'Student STK';
    if (value === 'offline_c2b') return 'Offline C2B';
    return value ? value.replace(/_/g, ' ') : 'Manual / Legacy';
  }, []);

  const formatReason = useCallback((value) => {
    if (!value) return 'Not recorded';
    return value.replace(/_/g, ' ');
  }, []);

  const statusTone = useCallback((value) => {
    const normalizedValue = String(value || '').toLowerCase();

    if (!normalizedValue) return 'neutral';
    if (normalizedValue.includes('fail') || normalizedValue.includes('cancel') || normalizedValue.includes('error')) return 'danger';
    if (normalizedValue.includes('pending') || normalizedValue.includes('review')) return 'warning';
    if (normalizedValue.includes('success') || normalizedValue.includes('recorded') || normalizedValue.includes('paid')) return 'success';
    if (normalizedValue.includes('process')) return 'info';
    return 'neutral';
  }, []);

  const viewStudent = useCallback((userId) => {
    if (!userId) return;
    navigate('/view-student', { state: { myData: userId } });
  }, [navigate]);

  const studentDirectory = useMemo(() => {
    return students.map((student) => ({
      ...student,
      studentKey: getStudentIdentity(student),
      displayName: getStudentDisplayName(student),
      statusLabel: getStudentStatusLabel(student),
      searchBlob: buildStudentSearchBlob(student)
    }));
  }, [students]);

  const openMatchModal = useCallback((payment) => {
    setSelectedUnmatchedPayment(payment);
    const currentMatch = studentDirectory
      .map((student) => ({
        ...student,
        matchScore: scoreStudentMatch(payment, student).score
      }))
      .sort((left, right) => right.matchScore - left.matchScore)[0];
    setSelectedStudentId(currentMatch?.studentKey || '');
    setMatchSearchTerm('');
    setMatchModalShow(true);
  }, [studentDirectory]);

  const closeMatchModal = useCallback(() => {
    if (savingMatch) return;
    setMatchModalShow(false);
    setSelectedUnmatchedPayment(null);
    setSelectedStudentId('');
    setMatchSearchTerm('');
  }, [savingMatch]);

  const saveMatchedStudent = useCallback(async () => {
    if (!selectedUnmatchedPayment || !selectedStudentId) {
      return;
    }

    const selectedStudent = studentDirectory.find((student) => student.studentKey === selectedStudentId);
    if (!selectedStudent) {
      return;
    }

    const receiptNumber = selectedUnmatchedPayment.transId
      || selectedUnmatchedPayment.billRefNumber
      || selectedUnmatchedPayment.rawBillRefNumber
      || selectedUnmatchedPayment.id;
    const paymentTimestamp = toTimestampMs(
      selectedUnmatchedPayment.paymentDate
      || selectedUnmatchedPayment.updatedAt
      || selectedUnmatchedPayment.createdAt
      || new Date()
    );
    const paymentAmount = Number(selectedUnmatchedPayment.amount || 0);
    const paymentDateValue = new Date(paymentTimestamp || Date.now());
    const paymentMethod = selectedUnmatchedPayment.paymentMethod || 'Mpesa Paybill';
    const emailAddress = selectedStudent.email || selectedStudent.userEmail || '';
    const phoneNumber = selectedStudent.phoneNumber || selectedStudent.phone || selectedStudent.mobile || '';
    const studentName = selectedStudent.displayName;
    const authUser = auth.currentUser;

    try {
      setSavingMatch(true);
      const incomeRef = doc(collection(db, 'Income'));

      await updateDoc(doc(db, 'UnmatchedFeePayments', selectedUnmatchedPayment.id), {
        studentId: selectedStudent.studentKey,
        studentName: selectedStudent.displayName,
        studentStatus: selectedStudent.statusLabel,
        studentCourse: selectedStudent.course || '',
        studentPhone: phoneNumber,
        studentEmail: emailAddress,
        studentMatched: true,
        matchedAt: Timestamp.now(),
        matchedBy: auth.currentUser?.email || auth.currentUser?.displayName || 'manual-audit',
        matchedReason: `Matched from payment audit (${receiptNumber})`,
        matchedReceiptNumber: receiptNumber
      });

      await setDoc(incomeRef, {
        studentId: selectedStudent.studentKey,
        studentName,
        studentEmail: emailAddress,
        studentPhone: phoneNumber,
        incomeAmount: paymentAmount,
        paymentDate: Timestamp.fromMillis(paymentTimestamp || Date.now()),
        paymentMethod,
        paymentChannel: 'manual_audit',
        paymentCategory: 'tuition',
        createdBy: authUser?.uid || 'manual-audit',
        createdByEmail: authUser?.email || '',
        timeCreated: Timestamp.now(),
        paymentId: incomeRef.id,
        invoiceNumber: receiptNumber,
        sourceCollection: 'UnmatchedFeePayments',
        sourcePaymentId: selectedUnmatchedPayment.id,
        sourceTransId: selectedUnmatchedPayment.transId || '',
        sourceBillRefNumber: selectedUnmatchedPayment.billRefNumber || '',
        matchedBy: authUser?.email || authUser?.displayName || 'manual-audit'
      });

      const emailMessage = buildReceiptEmail({
        studentName,
        amount: paymentAmount,
        paymentDate: paymentDateValue,
        paymentMethod,
        receiptNumber
      });

      const emailSubject = `Payment Receipt #${receiptNumber}`;
      const sendEmailPromise = emailAddress && authUser?.getIdToken
        ? authUser.getIdToken().then((token) => fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            email: emailAddress,
            subject: emailSubject,
            message: emailMessage
          })
        }))
        : Promise.resolve({ skipped: true });

      const smsMessage = `Dear ${studentName}, we have received your payment of KES. ${paymentAmount.toLocaleString('en-KE')} on ${paymentDateValue.toLocaleDateString('en-KE')}. View your receipt here https://live.ubunifucollege.com/view-receipt?${receiptNumber}. Thank you for your timely payment. - Ubunifu College`;
      const sendSmsPromise = phoneNumber
        ? fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phoneNumber,
            message: smsMessage
          })
        })
        : Promise.resolve({ skipped: true });

      const notificationResults = await Promise.allSettled([sendEmailPromise, sendSmsPromise]);

      const failedNotifications = notificationResults.filter((result) => result.status === 'rejected').length;

      await loadAuditData();
      setMatchModalShow(false);
      setSelectedUnmatchedPayment(null);
      setSelectedStudentId('');
      setMatchSearchTerm('');

      swal(
        'Matched',
        failedNotifications > 0
          ? 'Payment matched and recorded. One or more notifications may not have sent.'
          : 'Payment matched, recorded, and notifications sent.',
        failedNotifications > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      console.error('Failed to match payment to student:', error);
      swal('Error', 'Could not match and record the payment right now.', 'error');
    } finally {
      setSavingMatch(false);
    }
  }, [auth, db, loadAuditData, selectedStudentId, selectedUnmatchedPayment, studentDirectory]);

  const filteredSessions = useMemo(() => (
    sessions.filter((session) => matchesSearch(searchTerm, [
      session.studentName,
      session.studentEmail,
      session.studentId,
      session.phoneNumber,
      session.courseName,
      session.paymentType,
      session.checkoutRequestId,
      session.processingStatus,
      session.autoAdmissionReason,
    ]))
  ), [sessions, searchTerm]);

  const filteredOfflineTransactions = useMemo(() => (
    offlineTransactions.filter((transaction) => matchesSearch(searchTerm, [
      transaction.studentName,
      transaction.studentEmail,
      transaction.studentId,
      transaction.payerName,
      transaction.msisdn,
      transaction.billRefNumber,
      transaction.rawBillRefNumber,
      transaction.transId,
      transaction.processingStatus,
      transaction.autoAdmissionReason,
    ]))
  ), [offlineTransactions, searchTerm]);

  const unmatchedPaymentsWithSuggestions = useMemo(() => {
    return unmatchedPayments
      .filter((payment) => !payment.studentMatched)
      .map((payment) => {
        const suggestions = studentDirectory
          .map((student) => {
            const match = scoreStudentMatch(payment, student);
            return {
              ...student,
              matchScore: match.score,
              matchReasons: match.reasons
            };
          })
          .sort((left, right) => right.matchScore - left.matchScore)
          .slice(0, 5);

        return {
          ...payment,
          matchSuggestions: suggestions,
          bestMatch: suggestions[0] || null
        };
      });
  }, [studentDirectory, unmatchedPayments]);

  const filteredUnmatchedPayments = useMemo(() => (
    unmatchedPaymentsWithSuggestions.filter((payment) => matchesSearch(searchTerm, [
      payment.payerName,
      payment.msisdn,
      payment.billRefNumber,
      payment.transId,
      payment.reason,
      payment.bestMatch?.displayName,
      payment.bestMatch?.statusLabel,
      payment.bestMatch?.phoneNumber,
      payment.bestMatch?.course
    ]))
  ), [searchTerm, unmatchedPaymentsWithSuggestions]);

  const filteredIncomeAuditRows = useMemo(() => (
    incomeAuditRows.filter((item) => matchesSearch(searchTerm, [
      item.studentId,
      item.paymentMethod,
      item.paymentChannel,
      item.paymentCategory,
      item.paymentId,
      item.invoiceNumber,
      item.admissionTriggerReason,
    ]))
  ), [incomeAuditRows, searchTerm]);

  const sessionActionRows = useMemo(() => (
    filteredSessions
      .filter((session) => {
        const paymentStatus = String(session.paymentStatus || '').toLowerCase();
        const processingStatus = String(session.processingStatus || '').toLowerCase();

        return (
          paymentStatus === 'pending' ||
          paymentStatus === 'failed' ||
          processingStatus === 'pending' ||
          processingStatus.includes('fail')
        );
      })
      .map((session) => ({
        id: `session-${session.id}`,
        source: 'Student STK',
        status: session.processingStatus || session.paymentStatus || 'pending',
        when: toTimestampMs(session.updatedAt || session.createdAt),
        primaryText: session.studentName || session.studentId || 'Unknown student',
        secondaryText: `${formatPaymentType(session.paymentType)} • ${formatAmount(session.amountKes, session.currency)}`,
        detailText: session.checkoutRequestId || session.customerMessage || session.id,
      }))
  ), [filteredSessions, formatAmount, formatPaymentType]);

  const offlineActionRows = useMemo(() => (
    filteredOfflineTransactions
      .filter((transaction) => String(transaction.processingStatus || '').toLowerCase() !== 'recorded')
      .map((transaction) => ({
        id: `offline-${transaction.id}`,
        source: 'Offline C2B',
        status: transaction.processingStatus || 'needs_review',
        when: toTimestampMs(transaction.updatedAt || transaction.paymentDate || transaction.createdAt),
        primaryText: transaction.studentName || transaction.payerName || transaction.studentId || 'Unknown payer',
        secondaryText: `${formatPaymentType(transaction.paymentType)} • ${formatAmount(transaction.amount, transaction.currency)}`,
        detailText: transaction.transId || transaction.billRefNumber || transaction.id,
      }))
  ), [filteredOfflineTransactions, formatAmount, formatPaymentType]);

  const unmatchedActionRows = useMemo(() => (
    filteredUnmatchedPayments.map((payment) => ({
      id: `unmatched-${payment.id}`,
      source: 'Unmatched',
      status: 'needs_review',
      when: toTimestampMs(payment.updatedAt || payment.createdAt),
      primaryText: payment.payerName || payment.msisdn || payment.transId || 'Unknown payer',
      secondaryText: formatAmount(payment.amount, 'KES'),
      detailText: payment.billRefNumber || payment.reason || payment.id,
    }))
  ), [filteredUnmatchedPayments, formatAmount]);

  const actionNeededRows = useMemo(() => (
    sortByNewest(
      [...sessionActionRows, ...offlineActionRows, ...unmatchedActionRows],
      (item) => item.when
    ).slice(0, 30)
  ), [offlineActionRows, sessionActionRows, unmatchedActionRows]);

  const autoAdmissionCount = useMemo(() => (
    filteredIncomeAuditRows.filter((item) => item.admissionTriggered).length
  ), [filteredIncomeAuditRows]);

  const pendingSessionCount = useMemo(() => (
    filteredSessions.filter((session) => String(session.paymentStatus || session.processingStatus || '').toLowerCase().includes('pending')).length
  ), [filteredSessions]);

  const failedSessionCount = useMemo(() => (
    filteredSessions.filter((session) => {
      const combinedStatus = `${session.paymentStatus || ''} ${session.processingStatus || ''}`.toLowerCase();
      return combinedStatus.includes('fail') || combinedStatus.includes('cancel');
    }).length
  ), [filteredSessions]);

  return (
    <div className="clientsComp incomePage paymentAuditPage paymentAuditMinimal">
      <header className="paymentAuditHeader">
        <span className="paymentAuditEyebrow">Finance / Reconciliation</span>
        <h1>Payment audit</h1>
        <p>Review student payment sessions, M-Pesa C2B transactions, and unmatched payments.</p>
      </header>

      <div className="incomeStatsGrid paymentAuditOverview">
        <div className="incomeStatCard paymentAuditMetric">
          <span className="incomeStatLabel">Action Needed</span>
          <h3>{actionNeededRows.length}</h3>
        </div>
        <div className="incomeStatCard paymentAuditMetric">
          <span className="incomeStatLabel">Pending Student STK</span>
          <h3>{pendingSessionCount}</h3>
        </div>
        <div className="incomeStatCard paymentAuditMetric">
          <span className="incomeStatLabel">Failed Student STK</span>
          <h3>{failedSessionCount}</h3>
        </div>
        <div className="incomeStatCard paymentAuditMetric">
          <span className="incomeStatLabel">Auto Admissions Triggered</span>
          <h3>{autoAdmissionCount}</h3>
        </div>
      </div>

      <div className="paymentAuditToolbar">
        <input
          type="search"
          className="form-control paymentAuditSearchInput"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search names, phone, receipt, or reference"
          aria-label="Search payment audit records"
        />
      </div>

      <div className="paymentAuditTabs" role="tablist" aria-label="Payment audit sections">
        <button
          id="payment-audit-tab-action-needed"
          type="button"
          role="tab"
          aria-selected={activeTab === 'action-needed'}
          aria-controls="payment-audit-panel-action-needed"
          className={`paymentAuditTab ${activeTab === 'action-needed' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('action-needed')}
        >
          Action Needed <span>{actionNeededRows.length}</span>
        </button>
        <button
          id="payment-audit-tab-student-sessions"
          type="button"
          role="tab"
          aria-selected={activeTab === 'student-payment-sessions'}
          aria-controls="payment-audit-panel-student-sessions"
          className={`paymentAuditTab ${activeTab === 'student-payment-sessions' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('student-payment-sessions')}
        >
          Student Payment Sessions <span>{filteredSessions.length}</span>
        </button>
        <button
          id="payment-audit-tab-mpesa-c2b"
          type="button"
          role="tab"
          aria-selected={activeTab === 'mpesa-c2b-transactions'}
          aria-controls="payment-audit-panel-mpesa-c2b"
          className={`paymentAuditTab ${activeTab === 'mpesa-c2b-transactions' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('mpesa-c2b-transactions')}
        >
          Mpesa C2B Transactions <span>{filteredOfflineTransactions.length}</span>
        </button>
        <button
          id="payment-audit-tab-unmatched"
          type="button"
          role="tab"
          aria-selected={activeTab === 'unmatched-payments'}
          aria-controls="payment-audit-panel-unmatched"
          className={`paymentAuditTab ${activeTab === 'unmatched-payments' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('unmatched-payments')}
        >
          Unmatched Payments <span>{filteredUnmatchedPayments.length}</span>
        </button>
        <button
          id="payment-audit-tab-income-audit"
          type="button"
          role="tab"
          aria-selected={activeTab === 'income-audit-trail'}
          aria-controls="payment-audit-panel-income-audit"
          className={`paymentAuditTab ${activeTab === 'income-audit-trail' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('income-audit-trail')}
        >
          Income Audit Trail <span>{filteredIncomeAuditRows.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading payment audit records...</p>
        </div>
      ) : (
        <>
          {activeTab === 'action-needed' && (
          <section id="payment-audit-panel-action-needed" role="tabpanel" aria-labelledby="payment-audit-tab-action-needed" className="incomeTableCard paymentAuditTableCard">
            <div className="incomeTableHeader">
              <div>
                <h6>Action Needed</h6>
                <p>The newest items that support or finance should review first.</p>
              </div>
            </div>
            {actionNeededRows.length === 0 ? (
              <div className="incomeStateCard incomeEmptyState paymentAuditCompactState">
                <h6>No pending issues in the loaded records</h6>
                <p>Everything currently loaded is either matched and recorded or already resolved.</p>
              </div>
            ) : (
              <div className="paymentAuditActionList">
                {actionNeededRows.map((item) => (
                  <div key={item.id} className="paymentAuditActionRow">
                    <div className="paymentAuditActionMeta">
                      <strong>{item.primaryText}</strong>
                      <span>{item.secondaryText}</span>
                      <span>{item.detailText}</span>
                    </div>
                    <div className="paymentAuditActionSide">
                      <span className={`paymentAuditStatusPill paymentAuditStatusPill--${statusTone(item.status)}`}>{item.source}</span>
                      <span className={`paymentAuditStatusPill paymentAuditStatusPill--${statusTone(item.status)}`}>{item.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          )}

          {activeTab === 'student-payment-sessions' && (
          <section id="payment-audit-panel-student-sessions" role="tabpanel" aria-labelledby="payment-audit-tab-student-sessions" className="paymentAuditGrid paymentAuditGridSingle">
            <article className="incomeTableCard paymentAuditTableCard">
              <div className="incomeTableHeader">
                <div>
                  <h6>Student Payment Sessions</h6>
                  <p>Latest STK initiation and completion records from the student portal.</p>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="align-middle incomeRecordsTable">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Student</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Auto Admission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.length === 0 ? (
                      <tr key="empty"><td colSpan={5} className="paymentAuditTableEmpty">No student payment sessions match this search.</td></tr>
                    ) : filteredSessions.slice(0, 20).map((session) => (
                      <tr key={session.id}>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatDateTime(session.createdAt || session.updatedAt)}</strong>
                            <span>{session.checkoutRequestId || session.id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditStudentCell">
                            <button type="button" className="paymentAuditStudentLink" onClick={() => viewStudent(session.studentId)}>
                              {session.studentName || session.studentId || 'Unknown student'}
                            </button>
                            <span>{session.phoneNumber || session.studentEmail || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatPaymentType(session.paymentType)}</strong>
                            <span>{formatAmount(session.amountKes, session.currency)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditPillStack">
                            <span className={`paymentAuditStatusPill paymentAuditStatusPill--${statusTone(session.paymentStatus)}`}>{session.paymentStatus || '-'}</span>
                            <span className={`paymentAuditStatusPill paymentAuditStatusPill--${statusTone(session.processingStatus)}`}>{session.processingStatus || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{session.autoAdmitted ? 'Triggered admission' : 'No trigger'}</strong>
                            <span>{formatReason(session.autoAdmissionReason)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </article>
          </section>
          )}

          {activeTab === 'mpesa-c2b-transactions' && (
          <section id="payment-audit-panel-mpesa-c2b" role="tabpanel" aria-labelledby="payment-audit-tab-mpesa-c2b" className="paymentAuditGrid paymentAuditGridSingle">

            <article className="incomeTableCard paymentAuditTableCard">
              <div className="incomeTableHeader">
                <div>
                  <h6>Mpesa C2B Transactions</h6>
                  <p>Offline paybill collections, including match status and auto-admission outcome.</p>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="align-middle incomeRecordsTable">
                  <thead>
                    <tr>
                      <th>Paid At</th>
                      <th>Payer</th>
                      <th>Amount</th>
                      <th>Reference</th>
                      <th>Status</th>
                      <th>Auto Admission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOfflineTransactions.length === 0 ? (
                      <tr key="empty"><td colSpan={6} className="paymentAuditTableEmpty">No M-Pesa C2B transactions match this search.</td></tr>
                    ) : filteredOfflineTransactions.slice(0, 20).map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatDateTime(transaction.paymentDate || transaction.updatedAt)}</strong>
                            <span>{transaction.transId || transaction.id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditStudentCell">
                            <button type="button" className="paymentAuditStudentLink" onClick={() => viewStudent(transaction.studentId)}>
                              {transaction.studentName || transaction.payerName || transaction.studentId || 'Unknown payer'}
                            </button>
                            <span>{transaction.msisdn || transaction.studentEmail || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatAmount(transaction.amount, transaction.currency)}</strong>
                            <span>{formatPaymentType(transaction.paymentType)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{transaction.billRefNumber || transaction.rawBillRefNumber || '-'}</strong>
                            <span>{transaction.matchedBy || 'No match field saved'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditPillStack">
                            <span className={`paymentAuditStatusPill paymentAuditStatusPill--${statusTone(transaction.processingStatus)}`}>{transaction.processingStatus || '-'}</span>
                            <span className={`paymentAuditStatusPill paymentAuditStatusPill--${transaction.studentMatched ? 'success' : 'warning'}`}>
                              {transaction.studentMatched ? 'matched' : 'unmatched'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{transaction.autoAdmitted ? 'Triggered admission' : 'No trigger'}</strong>
                            <span>{formatReason(transaction.autoAdmissionReason)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </article>
          </section>
          )}

          {activeTab === 'unmatched-payments' && (
          <section id="payment-audit-panel-unmatched" role="tabpanel" aria-labelledby="payment-audit-tab-unmatched" className="paymentAuditGrid paymentAuditGridSingle">
            <article className="incomeTableCard paymentAuditTableCard">
              <div className="incomeTableHeader">
                <div>
                  <h6>Unmatched Payments</h6>
                  <p>These paybill transactions need manual follow-up or a corrected account reference.</p>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="align-middle incomeRecordsTable">
                  <thead>
                    <tr>
                      <th>Received</th>
                      <th>Payer</th>
                      <th>Bill Ref</th>
                      <th>Amount</th>
                      <th>Reason</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnmatchedPayments.length === 0 ? (
                      <tr key="empty"><td colSpan={6} className="paymentAuditTableEmpty">No unmatched payments need review.</td></tr>
                    ) : filteredUnmatchedPayments.slice(0, 20).map((payment) => (
                      <tr key={payment.id}>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatDateTime(payment.createdAt || payment.updatedAt)}</strong>
                            <span>{payment.transId || payment.id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditStudentCell">
                            <strong>{payment.payerName || 'Unknown payer'}</strong>
                            <span>{payment.msisdn || '-'}</span>
                          </div>
                        </td>
                        <td>{payment.billRefNumber || '-'}</td>
                        <td><span className="incomeAmountText">{formatAmount(payment.amount, 'KES')}</span></td>
                        <td>
                          <span className="paymentAuditReasonText">{payment.reason || 'Needs review'}</span>
                        </td>
                        <td>
                          <button type="button" className="paymentAuditStudentLink" onClick={() => openMatchModal(payment)}>
                            Match student
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </article>
          </section>
          )}

          <Modal show={matchModalShow} onHide={closeMatchModal} centered size="lg" contentClassName="paymentAuditModal">
            <Modal.Header closeButton>
              <Modal.Title>Match unmatched payment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="paymentAuditMatchIntro">
                <p>
                  Choose the student this payment belongs to. The search includes every student record in the system,
                  regardless of their current status.
                </p>
                {selectedUnmatchedPayment ? (
                  <div className="paymentAuditMatchSummary">
                    <strong>{selectedUnmatchedPayment.payerName || selectedUnmatchedPayment.msisdn || selectedUnmatchedPayment.transId || 'Unknown payer'}</strong>
                    <span>{selectedUnmatchedPayment.billRefNumber || 'No bill reference'}</span>
                    <span>{formatAmount(selectedUnmatchedPayment.amount, 'KES')}</span>
                  </div>
                ) : null}
              </div>

              <Form.Control
                type="text"
                className="paymentAuditMatchSearch"
                value={matchSearchTerm}
                onChange={(event) => setMatchSearchTerm(event.target.value)}
                placeholder="Search student by name, phone, id, course, or status..."
              />

              <Form.Select
                className="paymentAuditMatchSelect"
                value={selectedStudentId}
                onChange={(event) => setSelectedStudentId(event.target.value)}
              >
                <option value="">Select student</option>
                {studentDirectory
                  .filter((student) => {
                    if (!matchSearchTerm.trim()) return true;
                    return student.searchBlob.includes(matchSearchTerm.trim().toLowerCase());
                  })
                  .sort((left, right) => {
                    const leftScore = selectedUnmatchedPayment ? scoreStudentMatch(selectedUnmatchedPayment, left).score : 0;
                    const rightScore = selectedUnmatchedPayment ? scoreStudentMatch(selectedUnmatchedPayment, right).score : 0;
                    return rightScore - leftScore;
                  })
                  .slice(0, 50)
                  .map((student) => (
                    <option key={student.studentKey || student.id} value={student.studentKey}>
                      {student.displayName} • {student.statusLabel} • {student.course || 'Course not set'}
                    </option>
                  ))}
              </Form.Select>

              {selectedStudentId ? (() => {
                const chosenStudent = studentDirectory.find((student) => student.studentKey === selectedStudentId);
                if (!chosenStudent) return null;
                return (
                  <div className="paymentAuditMatchPreview">
                    <div>
                      <strong>{chosenStudent.displayName}</strong>
                      <span>{chosenStudent.statusLabel}</span>
                    </div>
                    <div>
                      <span>{chosenStudent.course || 'Course not set'}</span>
                      <span>{chosenStudent.phoneNumber || chosenStudent.userEmail || 'No contact recorded'}</span>
                    </div>
                  </div>
                );
              })() : null}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeMatchModal} disabled={savingMatch}>
                Cancel
              </Button>
              <Button variant="dark" onClick={saveMatchedStudent} disabled={savingMatch || !selectedStudentId}>
                {savingMatch ? 'Saving...' : 'Confirm match'}
              </Button>
            </Modal.Footer>
          </Modal>

          {activeTab === 'income-audit-trail' && (
          <section id="payment-audit-panel-income-audit" role="tabpanel" aria-labelledby="payment-audit-tab-income-audit" className="paymentAuditGrid paymentAuditGridSingle">
            <article className="incomeTableCard paymentAuditTableCard">
              <div className="incomeTableHeader">
                <div>
                  <h6>Income Audit Trail</h6>
                  <p>Recent `Income` rows tagged with payment channel and admission-trigger metadata.</p>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="align-middle incomeRecordsTable">
                  <thead>
                    <tr>
                      <th>Recorded</th>
                      <th>Student</th>
                      <th>Amount</th>
                      <th>Channel</th>
                      <th>Admission Audit</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIncomeAuditRows.length === 0 ? (
                      <tr key="empty"><td colSpan={6} className="paymentAuditTableEmpty">No income audit rows match this search.</td></tr>
                    ) : filteredIncomeAuditRows.slice(0, 20).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{formatDateTime(item.paymentDate || item.createdAt)}</strong>
                            <span>{item.paymentCategory || item.source || item.id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paymentAuditStudentCell">
                            <button type="button" className="paymentAuditStudentLink" onClick={() => viewStudent(item.studentId)}>
                              {item.studentId || 'Unknown student'}
                            </button>
                            <span>{item.paymentMethod || '-'}</span>
                          </div>
                        </td>
                        <td><span className="incomeAmountText">{formatAmount(item.incomeAmount || item.amountInKes, item.currency)}</span></td>
                        <td><span className="incomeMethodBadge">{formatChannel(item.paymentChannel)}</span></td>
                        <td>
                          <div className="incomeDateCell">
                            <strong>{item.admissionTriggered ? 'Triggered admission' : 'No trigger'}</strong>
                            <span>{formatReason(item.admissionTriggerReason)}</span>
                          </div>
                        </td>
                        <td>{item.invoiceNumber || item.paymentId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </article>
          </section>
          )}
        </>
      )}
    </div>
  );
}

export default PaymentAuditComp;
